import React from 'react';
import { 
  UserGroupIcon, 
  DocumentTextIcon, 
  ClipboardDocumentListIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<any>;
  color: string;
  change?: string;
  changeType?: 'positive' | 'negative';
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  change, 
  changeType 
}) => {
  return (
    <div className="card">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-xs font-medium text-secondary-600">{title}</p>
          <p className="text-2xl font-bold text-secondary-900">{value}</p>
          {change && (
            <p className={`text-sm ${changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
              {change}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const stats = [
    {
      title: 'Total de Clientes',
      value: '245',
      icon: UserGroupIcon,
      color: 'bg-primary-600',
      change: '+12% este mês',
      changeType: 'positive' as const,
    },
    {
      title: 'Propostas Abertas',
      value: '18',
      icon: DocumentTextIcon,
      color: 'bg-yellow-600',
      change: '+3 esta semana',
      changeType: 'positive' as const,
    },
    {
      title: 'Contratos Ativos',
      value: '67',
      icon: ClipboardDocumentListIcon,
      color: 'bg-green-600',
      change: '+8% este mês',
      changeType: 'positive' as const,
    },
    {
      title: 'Taxa de Conversão',
      value: '73%',
      icon: CheckCircleIcon,
      color: 'bg-purple-600',
      change: '+5% este mês',
      changeType: 'positive' as const,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-base font-bold text-secondary-900">Dashboard</h1>
        <p className="text-secondary-600">
          Bem-vindo ao sistema Lexpress. Aqui está um resumo das suas atividades.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">
            Ações Rápidas
          </h3>
          <div className="space-y-3">
            <button className="w-full text-left p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
              <div className="font-medium text-secondary-900">Novo Cliente</div>
              <div className="text-sm text-secondary-600">Cadastrar um novo cliente</div>
            </button>
            <button className="w-full text-left p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
              <div className="font-medium text-secondary-900">Nova Proposta</div>
              <div className="text-sm text-secondary-600">Criar uma nova proposta comercial</div>
            </button>
            <button className="w-full text-left p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
              <div className="font-medium text-secondary-900">Relatório</div>
              <div className="text-sm text-secondary-600">Gerar relatório de vendas</div>
            </button>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">
            Atividades Recentes
          </h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-xs font-medium text-secondary-900">
                  Contrato assinado - Cliente ABC
                </p>
                <p className="text-xs text-secondary-600">2 horas atrás</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-xs font-medium text-secondary-900">
                  Proposta enviada - Cliente XYZ
                </p>
                <p className="text-xs text-secondary-600">4 horas atrás</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-xs font-medium text-secondary-900">
                  Novo cliente cadastrado
                </p>
                <p className="text-xs text-secondary-600">1 dia atrás</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;